import {
  BarChart3,
  BookOpenText,
  Bot,
  ChartNoAxesCombined,
  ChevronDown,
  Database,
  FileChartColumn,
  FlaskConical,
  LayoutDashboard,
  LogOut,
  Menu,
  Moon,
  Package,
  PanelLeftClose,
  PanelLeftOpen,
  Shuffle,
  Sun,
  UserRound,
  X,
} from "lucide-react";
import { useEffect, useMemo, useState, type ReactNode } from "react";
import { DEFAULT_ANALYST_NAME } from "../services/dataDomain";
import type { PageId, UserSession } from "../types";
import { Brand } from "./Brand";

interface AppShellProps {
  page: PageId;
  session: UserSession;
  /** Named from the selected source's data domain. */
  analystName?: string;
  onNavigate: (page: PageId) => void;
  onLogout: () => void;
  children: ReactNode;
}

const pageDetails: Record<PageId, { title: string; subtitle: string }> = {
  assistants: {
    title: "Select assistant",
    subtitle: "",
  },
  analyst: {
    title: DEFAULT_ANALYST_NAME,
    subtitle: "Create governed dashboards through guided conversation",
  },
  dashboards: {
    title: "Dashboards",
    subtitle: "Select a DMI template or compose a new dashboard",
  },
  explore: {
    title: "Drill-down & Detail",
    subtitle: "Reversible drill paths over governed metrics",
  },
  reports: {
    title: "Report Library",
    subtitle: "Scheduled deliverables, scorecards, and alerts",
  },
  "report-builder": {
    title: "Report Builder",
    subtitle: "Narratives and charts from governed content",
  },
  advanced: {
    title: "Advanced Analytics",
    subtitle: "Predictive, historical, anomaly, and root-cause views",
  },
  "what-if": {
    title: "What-If Studio",
    subtitle: "Driver-based scenarios with versioned assumptions",
  },
  workspace: {
    title: "Workspace",
    subtitle: "Your private dashboard workspace",
  },
  "my-dashboards": {
    title: "My Dashboards",
    subtitle: "Your private dashboard workspace",
  },
  "my-reports": {
    title: "My Reports",
    subtitle: "Your private report workspace",
  },
  "my-artifacts": {
    title: "My Artifacts",
    subtitle: "Your private artifact workspace",
  },
  "dashboard-canvas": {
    title: "Create dashboard",
    subtitle: "One question per box, answered from a governed semantic model",
  },
  "report-canvas": {
    title: "Create report",
    subtitle: "Section by section, with every figure from a governed semantic model",
  },
  "artifact-canvas": {
    title: "Create artifact",
    subtitle: "Section by section, with every figure from a governed semantic model",
  },
  "data-sources": {
    title: "Semantic Model Inventory",
    subtitle: "Published semantic models, their scope and readiness",
  },
  metrics: {
    title: "Metrics Catalog",
    subtitle: "Certified definitions and new metric workflow",
  },
  trust: {
    title: "Trust & Governance",
    subtitle: "Audit trail, policy, freshness, and evaluation",
  },
  groups: {
    title: "Groups",
    subtitle: "Who belongs together, and therefore what they can open",
  },
};

/** The menu entry highlighted while a canvas is open. */
const canvasParents: Partial<Record<PageId, PageId>> = {
  "dashboard-canvas": "my-dashboards",
  "report-canvas": "my-reports",
  "artifact-canvas": "my-artifacts",
};

const navGroups: Array<{
  label: string;
  /** Kept in the list so it can be switched back on; not rendered. */
  hidden?: boolean;
  items: Array<{ id: PageId; label: string; icon: typeof Bot }>;
}> = [
  {
    label: "My Workspace",
    items: [
      { id: "my-dashboards", label: "Dashboards", icon: LayoutDashboard },
      { id: "my-reports", label: "Reports", icon: FileChartColumn },
      { id: "my-artifacts", label: "Artifacts", icon: Package },
    ],
  },
  {
    label: "AI Assistant",
    // Choosing comes before asking, in the order the work happens: the landing
    // page is where a session starts, and this is also the way back to it.
    items: [
      { id: "assistants", label: "Select assistant", icon: Shuffle },
      { id: "analyst", label: "AI Analyst", icon: Bot },
    ],
  },
  {
    label: "Dashboards",
    hidden: true,
    items: [
      { id: "dashboards", label: "Dashboards", icon: LayoutDashboard },
      { id: "explore", label: "Drill-down & Detail", icon: ChartNoAxesCombined },
    ],
  },
  {
    label: "Reports",
    hidden: true,
    items: [
      { id: "reports", label: "Report Library", icon: BookOpenText },
      { id: "report-builder", label: "Report Builder", icon: FileChartColumn },
    ],
  },
  {
    label: "Advanced",
    items: [
      { id: "advanced", label: "Advanced Analytics", icon: BarChart3 },
      { id: "what-if", label: "What-If Studio", icon: FlaskConical },
    ],
  },
  {
    label: "Admin Control Panel",
    items: [
      {
        id: "data-sources",
        label: "Semantic Model Inventory",
        icon: Database,
      },
      { id: "groups", label: "Groups", icon: UserRound },
    ],
  },
];

export function AppShell({
  page,
  session,
  analystName = DEFAULT_ANALYST_NAME,
  onNavigate,
  onLogout,
  children,
}: AppShellProps) {
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const [dark, setDark] = useState(false);
  const details =
    page === "analyst"
      ? { ...pageDetails.analyst, title: analystName }
      : pageDetails[page];
  const labelFor = (id: PageId, label: string) =>
    id === "analyst" ? analystName : label;
  // Canvases are opened from their workspace list and have no menu entry.
  const activeId: PageId = canvasParents[page] ?? page;
  const initials = useMemo(
    () =>
      session.displayName
        .split(" ")
        .map((part) => part[0])
        .join("")
        .slice(0, 2)
        .toUpperCase(),
    [session.displayName],
  );

  useEffect(() => {
    document.documentElement.dataset.theme = dark ? "dark" : "light";
  }, [dark]);

  function navigate(next: PageId) {
    onNavigate(next);
    setMobileOpen(false);
  }

  const isAdmin = session.roles?.some((role) => {
    const name = role.toLowerCase();
    return name === "admin" || name === "superadmin";
  });
  const sidebar = (
    <aside className={`sidebar ${collapsed ? "collapsed" : ""}`}>
      <div className="sidebar-head">
        <Brand compact={collapsed} />
        <button
          type="button"
          className="icon-button desktop-only"
          onClick={() => setCollapsed((value) => !value)}
          aria-label={collapsed ? "Expand navigation" : "Collapse navigation"}
        >
          {collapsed ? <PanelLeftOpen size={18} /> : <PanelLeftClose size={18} />}
        </button>
        <button
          type="button"
          className="icon-button mobile-only"
          onClick={() => setMobileOpen(false)}
          aria-label="Close navigation"
        >
          <X size={18} />
        </button>
      </div>
      <nav className="side-nav" aria-label="Primary navigation">
        {navGroups.filter((group) => !group.hidden).map((group) => (
          <div className="nav-group" key={group.label}>
            {!collapsed && <p>{group.label}</p>}
            {group.items
              .filter((item) => item.id !== "groups" || isAdmin)
              .map(({ id, label, icon: Icon }) => (
              <button
                type="button"
                key={id}
                className={activeId === id ? "active" : ""}
                onClick={() => navigate(id)}
                aria-current={page === id ? "page" : undefined}
                title={collapsed ? labelFor(id, label) : undefined}
              >
                <Icon size={18} aria-hidden="true" />
                {!collapsed && <span>{labelFor(id, label)}</span>}
              </button>
            ))}
          </div>
        ))}
      </nav>
      <a
        className="dmi-link"
        href="https://www.dminc.com"
        target="_blank"
        rel="noreferrer"
      >
        {!collapsed ? "DMInc.com" : "DMI"}
      </a>
    </aside>
  );

  return (
    <div className={`app-layout ${collapsed ? "nav-collapsed" : ""}`}>
      <div className={`mobile-nav-layer ${mobileOpen ? "open" : ""}`}>{sidebar}</div>
      <div className="desktop-sidebar">{sidebar}</div>

      <div className="app-main">
        <header className="topbar">
          <button
            type="button"
            className="icon-button mobile-only"
            onClick={() => setMobileOpen(true)}
            aria-label="Open navigation"
          >
            <Menu size={20} />
          </button>
          <div className="page-title">
            <strong>{details.title}</strong>
            {details.subtitle && <small>{details.subtitle}</small>}
          </div>
          <div className="topbar-actions">
            <button
              type="button"
              className="icon-button"
              aria-label={dark ? "Use light theme" : "Use dark theme"}
              onClick={() => setDark((value) => !value)}
            >
              {dark ? <Sun size={18} /> : <Moon size={18} />}
            </button>
            <div className="profile-menu">
              <button
                type="button"
                className="profile-button"
                onClick={() => setProfileOpen((value) => !value)}
                aria-expanded={profileOpen}
              >
                <span>{initials}</span>
                <div>
                  <strong>{session.displayName}</strong>
                  <small>Private workspace</small>
                </div>
                <ChevronDown size={15} />
              </button>
              {profileOpen && (
                <div className="profile-popover">
                  <div>
                    <UserRound size={17} />
                    <span>{session.email}</span>
                  </div>
                  <button type="button" onClick={onLogout}>
                    <LogOut size={17} />
                    Sign out
                  </button>
                </div>
              )}
            </div>
          </div>
        </header>
        <main className="page-content">
          {children}
        </main>
      </div>
    </div>
  );
}
